import {Platform} from "@variocube/app-api";
import {Badge, classNames, layout, NumberStepInput, PushButton, typography} from "@variocube/cube-ui";
import {DateTime, Duration} from "luxon";
import * as React from "react";
import {Fragment, useCallback, useEffect, useMemo, useState} from "react";
import {style, stylesheet} from "typestyle";
import {Accessibility, BookingPlan, BookingUnit, Cart, computeAmount, OccupancyBooking, PricingMode} from "./booking";
import {BoxItem} from "./BoxItem";
import infoIcon from "./info.svg";
import {compareTime, findAccessibility, formatCurrency, parseTime} from "./utils";
import {useLocalization} from "../../i18n";

interface ProlongBookingCartStepProps {
    platform: Platform;
    plans: BookingPlan[];
    maxAmount: number;
    onCart: (cart: Cart) => void;
    onCancel: () => void;
    onConfirmClearOut: () => void;
    onZeroAmount: () => void;
    accessibleFrom: string;
    accessibleUntil: string;
    accessibility: Accessibility;
    booking: OccupancyBooking;
    boxNumber: string;
    currency: string;
    pricingMode: PricingMode;
    bookingUnit: BookingUnit;
}


export function ProlongBookingCartStep(props: ProlongBookingCartStepProps) {
    const {
        onCancel,
        onConfirmClearOut,
        onZeroAmount,
        plans,
        maxAmount,
        boxNumber,
        booking,
        accessibleFrom,
        accessibleUntil,
        accessibility,
        currency,
        onCart,
        pricingMode,
        bookingUnit,
    } = props;
    const {t, language} = useLocalization();
    const [units, setUnits] = useState(1);

    const bookingStart = DateTime.fromISO(booking.from).setLocale(language);
    const bookingEnd = DateTime.fromISO(booking.until).setLocale(language);

    const newBookingEnd = useMemo(() => {
        if (bookingUnit == "Days") {
            let modifiedEnd = bookingEnd.plus(Duration.fromObject({days: units}))

            const {until} = findAccessibility(accessibility, accessibleFrom, accessibleUntil, modifiedEnd);
            const untilTime = parseTime(until) ?? {hour: 23, minute: 59, second: 59};

            return modifiedEnd.set(untilTime);
        }
        else {
            return bookingEnd.plus(Duration.fromObject({hours: units}));
        }
    }, [bookingUnit, bookingStart, bookingEnd, units, accessibility]);

    const plan = useMemo(() => plans.find(plan => plan.boxType == booking.boxType), []);

    const amount = useMemo(() => {
        if (plan) {
            return computeAmount(pricingMode, plan, units).finalAmount;
        }
    }, [plan, pricingMode, units]);

    useEffect(() => {
        if (pricingMode === 'Unit' && plan) {
            console.log('bookingEnd', bookingEnd)
            console.log('newBookingEnd', newBookingEnd)

            let minUnits = 1;
            if (bookingUnit === 'Days') {
                minUnits = Math.ceil(DateTime.now().diff(bookingEnd, 'days').get('days'));
            } else if (bookingUnit === 'Hours') {
                minUnits = Math.ceil(DateTime.now().diff(bookingEnd, 'hours').get('hours'));
            }
            let autoUnits = 0;
            for (let i = 1; i <= minUnits; i++) {
                let amount = computeAmount(pricingMode, plan, i)?.amount ?? -1;
                if (amount <= maxAmount) autoUnits = i;
                else break;
            }
            setUnits(autoUnits);
        }
    }, [])

    useEffect(() => {
        if (amount === 0) {
            onZeroAmount();
        }
    }, [amount]);

    const handleNext = useCallback(() => {
        if (plan && typeof amount != "undefined") {
            const {boxType} = plan;
            onCart({
                boxType,
                units,
                amount,
                from: bookingEnd,
                until: newBookingEnd,
            })
        }
    }, [plan, onCart, units, amount]);

    useEffect(() => {
        if (plan && pricingMode == "Package") {
            if (!plan.packages.find(packageLine => packageLine.quantity == units)) {
                const first = plan.packages[0];
                if (first) {
                    setUnits(first.quantity);
                }
            }
        }
    }, [plan, pricingMode, units]);

    if (plan && typeof amount != "undefined") {
        if (amount === 0) {
            return (
                <div className={classNames(layout.fill, layout.vertical, layout.spaceAround, layout.paddingLarge)}>
                    <div className={classNames(layout.fill, layout.vertical, layout.center)}>
                        <div className={typography.title}>{t('ClearOutConfirmationTitle')}</div>
                        <div className={style({marginTop: "1.5em"})}>{t('ClearOutConfirmationInfo')}</div>
                    </div>

                    <div className={classNames(layout.horizontal, layout.spaceBetween, styles.buttonArea)}>
                        <PushButton onClick={onCancel}>
                            {t("Cancel")}
                        </PushButton>

                        <PushButton primary onClick={onConfirmClearOut}>
                            {t("ClearOutConfirmationButton")}
                        </PushButton>
                    </div>
                </div>
            )
        }


        const maxAmountExceeded = amount > maxAmount;

        return (
            <div className={classNames(layout.fill, layout.vertical, layout.spaceAround, layout.paddingLarge)}>

                <div className={classNames(layout.fill, layout.vertical, layout.center)}>
                    <div className={layout.horizontal}>
                        <BoxItem number={boxNumber} />

                        <div className={style({marginLeft: "1.5em"})}>
                            <div className={typography.title}>
                                {t(bookingUnit == "Days" ? "PeriodInDays" : "PeriodInHours")}
                            </div>
                            <NumberStepInput
                                value={units}
                                min={1}
                                onChange={setUnits}
                                className={style({ margin: "0.75em 0"})}
                                options={
                                    (pricingMode == "Package" && plan)
                                        ? plan.packages
                                            .sort((a, b) => a.quantity - b.quantity)
                                            .map(packageLine => packageLine.quantity)
                                        : undefined
                                }
                            />
                            {bookingUnit == "Days" && (
                                <Fragment>
                                    <div>
                                        <strong>
                                            {bookingStart.toLocaleString(DateTime.DATE_SHORT)} - {newBookingEnd.toLocaleString(DateTime.DATE_SHORT)}
                                        </strong>
                                    </div>
                                    <div>
                                        {t("BookingPeriodInfo", { accessibleUntil })}
                                    </div>
                                </Fragment>
                            )}
                            {bookingUnit == "Hours" && (
                                <Fragment>
                                    <div>{t("BookingPeriodInfo", { accessibleUntil: "" })}</div>
                                    <div>
                                        <strong dangerouslySetInnerHTML={{ // because the english string containers html entities
                                            __html: newBookingEnd.toLocaleString(DateTime.DATETIME_SHORT)
                                        }}/>
                                    </div>
                                </Fragment>
                            )}
                        </div>
                    </div>

                    <div className={classNames(layout.horizontal, layout.spaceBetween, styles.infoAndAmount)}>

                        <div className={classNames(layout.horizontal, layout.alignCenter, styles.info)}>
                            <img src={infoIcon} className={styles.infoIcon} alt="Info"/>
                            <div>
                                {t("ProlongBookingFlowInfo")}
                            </div>
                        </div>

                        <div className={classNames(typography.alignRight, styles.amountContainer)}>
                            {!maxAmountExceeded && (
                                <div className={style({ fontSize: "2em"})}>
                                    {t("Amount")}
                                    {" "}
                                    <strong>{formatCurrency(amount, currency, language)}</strong>
                                </div>
                            )}

                            {maxAmountExceeded && (
                                <div>
                                    <Badge error>
                                        {t("MaxAmountExceeded", {maxAmount: formatCurrency(maxAmount, currency, language)})}
                                    </Badge>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className={classNames(layout.horizontal, layout.spaceBetween, styles.buttonArea)}>
                    <PushButton onClick={onCancel}>
                        {t("Cancel")}
                    </PushButton>

                    <PushButton primary onClick={handleNext} disabled={maxAmountExceeded}>
                        {t("Next")}
                    </PushButton>
                </div>
            </div>
        );
    }
    return null;
}

const styles = stylesheet({
    infoAndAmount: {
        borderTop: "1px solid var(--color-separator)",
        marginTop: "1.5em",
        paddingTop: "1em",
    },
    infoIcon: {
        width: "2.5em",
        marginRight: "0.75em"
    },
    info: {
        width: "30em"
    },
    amountContainer: {
        position: "relative",
        width: "20em",
    },
    buttonArea: {
        marginTop: "1.5em"
    }
});
